import { useQueryClient } from "@tanstack/react-query"

import { ISubscription } from "src/components/Account/types"
import {
  TBillingHistoryQueryParams,
  TFetchUserOrdersQueryParams,
  TPlanRenewalEstimateBreakdownFilter,
} from "src/data/billing/types/billingTypes"

export const billingKeys = {
  /** Use to target *all* billing data in the cache, with fuzzy matching; matching this
   * way is ineffective and should be used with care */
  all() {
    return ["billing"] as const
  },

  /** Use to target customer data in cache*/
  userBillingInfo(userId: string) {
    return [...this.all(), "userBillingInfo", userId] as const
  },

  /** Use to target available plas for user in cache */
  availablePlans(userId: string) {
    return [...this.all(), "availablePlans", userId] as const
  },

  /** Use to target all subscription data in the cache, currently we only hold
   * one subscription due to fetching it is a anonymous get */
  subscription() {
    return [...this.all(), "subscription"] as const
  },

  /** Use to target current subscription data in cache*/
  currentSubscription(userId: string) {
    return [...this.all(), "current_subscription", userId] as const
  },

  /** Use to target payment source data in cache*/
  paymentSource(id?: string) {
    if (id) {
      return [...this.all(), "paymentSource", id] as const
    }

    return [...this.all(), "paymentSource"] as const
  },

  billingHistory(orgId: string, filter?: TBillingHistoryQueryParams) {
    if (filter) {
      return [...this.all(), "billingHistory", orgId, filter] as const
    }

    return [...this.all(), "billingHistory", orgId] as const
  },

  billingEvent(eventId: string) {
    return [...this.all(), "billingHistory", eventId] as const
  },

  userOrders(userId: string, filter?: TFetchUserOrdersQueryParams) {
    if (filter) {
      return [...this.all(), "userOrders", userId, filter] as const
    }

    return [...this.all(), "userOrders", userId] as const
  },

  userOrder(orderNumber: string) {
    return [...this.all(), "userOrder", orderNumber] as const
  },

  planRenewalEstimate(userId: string) {
    return [...this.all(), "planRenewalEstimate", userId] as const
  },

  planRenewalEstimateBreakdown(
    userId: string,
    filter?: TPlanRenewalEstimateBreakdownFilter
  ) {
    if (filter) {
      return [
        ...this.all(),
        "planRenewalEstimate",
        userId,
        "breakdown",
        filter,
      ] as const
    }

    return [...this.all(), "planRenewalEstimate", userId, "breakdown"] as const
  },

  allUserSubscriptions(customerId: string) {
    return [...this.all(), "allUserSubscriptions", customerId] as const
  },
}

export function useBillingCache() {
  const queryClient = useQueryClient()
  /**
   * Update subscription with new data, e.g., after a PATCH.
   *
   * @param subscriptionId: The subscription id
   * @param updater: Updater function which to modify customer in cache
   */
  function updateCachedSubscription(
    updater: (subscription: ISubscription | undefined) => ISubscription
  ) {
    queryClient.setQueryData<ISubscription>(billingKeys.subscription(), updater)
  }

  /**
   * Removes queries for cached payment source. Essentially clearing the cache and removing any queries that listened to them.
   */
  function removeCachedPaymentSource() {
    queryClient.removeQueries({ queryKey: billingKeys.paymentSource() })
  }

  async function invalidateCachedBillingInfo(userId: string) {
    await queryClient.invalidateQueries({
      queryKey: billingKeys.userBillingInfo(userId),
    })
  }

  return {
    invalidateCachedBillingInfo,
    updateCachedSubscription,
    removeCachedPaymentSource,
  }
}
